import { useEffect, useState, useCallback } from "react";
import { X, Plus, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TYPES = ["Appointment", "Deadline", "Consultation", "Task", "Hearing"];
const typeBadge = (t) => ({ Consultation: "bg-blue-100 text-blue-700", Deadline: "bg-red-100 text-red-700", Task: "bg-amber-100 text-amber-700", Appointment: "bg-emerald-100 text-emerald-700", Hearing: "bg-purple-100 text-purple-700" }[t] || "bg-slate-100 text-slate-600");
const dayKey = (iso) => (iso || "").slice(0, 10);
const fmtDay = (k) => { try { return new Date(k).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }); } catch { return k; } };
const fmtTime = (iso) => { try { const d = new Date(iso); return d.toTimeString().slice(0, 5) === "00:00" ? "All day" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function CalendarManager() {
  const { can } = useAuth();
  const [events, setEvents] = useState([]);
  const [owner, setOwner] = useState("all");
  const [staff, setStaff] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", type: "Appointment", start: "", notes: "" });

  const load = useCallback(() => {
    const params = {};
    if (owner !== "all") params.owner = owner;
    api.get("/admin/calendar", { params }).then((r) => setEvents(r.data)).catch(() => {});
  }, [owner]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/calendar", form); toast.success("Event added"); setCreating(false); setForm({ title: "", type: "Appointment", start: "", notes: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const groups = events.reduce((acc, e) => { const k = dayKey(e.start); (acc[k] = acc[k] || []).push(e); return acc; }, {});
  const days = Object.keys(groups).sort();

  return (
    <div data-testid="calendar-manager">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="font-serif text-3xl font-bold text-navy">Calendar</h1><p className="mt-1 text-sm text-slate-500">Consultations, deadlines and tasks in one diary.</p></div>
        <div className="flex gap-2">
          <select value={owner} onChange={(e) => setOwner(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="calendar-owner-filter"><option value="all">Everyone</option>{staff.map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}</select>
          {can("calendar", "create") && <button onClick={() => setCreating(true)} data-testid="new-event-btn" className="btn-gold text-sm"><Plus size={15} /> New event</button>}
        </div>
      </div>

      <div className="space-y-6">
        {days.map((k) => (
          <div key={k}>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-navy"><CalendarDays size={16} className="text-gold" /> {fmtDay(k)}</div>
            <div className="space-y-2">
              {groups[k].map((e) => (
                <div key={e.id} data-testid="calendar-event" className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
                  <span className="w-16 shrink-0 text-xs font-medium text-slate-500">{fmtTime(e.start)}</span>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${typeBadge(e.type)}`}>{e.type}</span>
                  <span className="flex-1 text-sm text-navy">{e.title}</span>
                  {e.owner && <span className="hidden text-xs text-slate-400 sm:block">{e.owner}</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
        {days.length === 0 && <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-400">No scheduled events.</div>}
      </div>

      {creating && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCreating(false)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-serif text-xl font-semibold text-navy">New event</h2><button onClick={() => setCreating(false)} className="text-slate-400"><X size={18} /></button></div>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Title</Label><Input data-testid="create-event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
                <div><Label>Date &amp; time</Label><Input type="datetime-local" data-testid="create-event-start" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} required className="mt-1" /></div>
              </div>
              <button type="submit" data-testid="create-event-submit" className="btn-gold w-full">Add event</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
